import { Box, Button, ButtonGroup, Checkbox, Collapse, Dialog, FormControl, IconButton, Menu, MenuItem, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'
import Latex from 'react-latex-next'
import { v4 as uuidv4 } from 'uuid'
import dayjs from 'dayjs'

import { ALPHABETS, Icons, latexDelimiters, POLL_OPTIONS_COLORS } from '../../../utils/utilities'
import { useSocketsContext } from '../../../contexts/sockets.context'
import { useMeetingStore } from '../../../store/meeting.store'
import CustomSwitch from '../../common/CustomSwitch'
import pollsImg from '../../../assets/polls.svg'
import CustomChip from '../../common/CustomChip'
import NextPollTimer from './NextPollTimer'
import PollQueue from './PollQueue'
import { useOnlineSessionsSocketStore } from '../../../store/onlinesessionsocket.store'

const pollDurationOptions = [
    { duration: "1 Min", value: 60 },
    { duration: "2 Min", value: 120 },
    { duration: "3 Min", value: 180 },
]


const autoPollIntervalOptions = [
    { label: "Stop Auto Polling", value: 0, buttonLabel: "Start Auto Polling" },
    { label: "Every 3 Minute", value: 3 },
    { label: "Every 4 Minute", value: 4 },
    { label: "Every 5 Minute", value: 5 },
]

const getPollManagerDialogTitle = mode => {
    switch ( mode ) {
        case "manage-polls":
            return { title: "Manage Polls", subtitle: "Create and manage poll for interactice session." }

        case "create":
            return { title: "Create New Poll", subtitle: "Provide a question and options for a new poll." }

        case "manage-poll-queue":
            return { title: "Manage Poll Queue", subtitle: "Send automated polls with poll queue and poll interval." }

        default: return {}
    }
}


const PollBar = ( { poll } ) => {

    const [showParticipants, setShowParticipants] = useState( false )
    const [collapsedOptions, setCollapsedOptions] = useState( true )
    const [remainingTime, setRemainingTime] = useState( () => {
        const timeLeft = Math.floor( ( new Date( poll.ends_at ) - Date.now() ) / 1000 )

        const remaining = ( timeLeft / poll.duration ) * 100

        return Math.min( remaining, 100 )
    } )

    const { palette, border } = useTheme()


    const toggleResultView = ( e ) => {
        e.stopPropagation()
        setShowParticipants( prev => !prev )
    }


    useEffect( () => {
        if ( !poll ) return

        const updateTimer = () => {
            const timeLeft = Math.floor( ( new Date( poll.ends_at ) - Date.now() ) / 1000 )
            const remaining = ( timeLeft / poll.duration ) * 100
            const clampedRemaining = Math.max( 0, Math.min( remaining, 100 ) )

            setRemainingTime( clampedRemaining )
            return timeLeft
        }

        const initialTimeLeft = updateTimer()
        if ( initialTimeLeft <= 0 ) return

        const interval = setInterval( () => {
            const timeLeft = updateTimer()

            if ( timeLeft <= 0 ) {
                clearInterval( interval )
            }
        }, 2000 )

        return () => clearInterval( interval )
    }, [poll] )


    return (
        <Box border={border[1]} borderRadius="10px" overflow="clip" key={poll.id}>
            <Box borderBottom={border[1]} position="relative" sx={{ cursor: "pointer", ":hover": { bgcolor: palette.rowHover } }} onClick={() => setCollapsedOptions( prev => !prev )} display="flex" alignItems="center" justifyContent="space-between" gap="20px" padding="10px">
                <Box>
                    <Typography fontSize="16px" component="div" fontWeight="500" >
                        <Latex delimiters={latexDelimiters}>{poll.question}</Latex>
                    </Typography>
                    <Typography fontSize="12px" color="GrayText" fontWeight="600" >{poll.totalVotes} votes {remainingTime === 0 ? "| ENDED" : "| ACTIVE"} </Typography>
                </Box>
                <Typography display="flex" alignItems="center" fontSize="20px">
                    {!collapsedOptions ? Icons.default.KeyboardArrowUp : Icons.default.KeyboardArrowDownIcon}
                </Typography>
            </Box>
            <Collapse in={!collapsedOptions} >
                <Box padding="0 5px">
                    <Box display="flex" gap="10px" flexWrap="wrap" marginY="10px">
                        {Number.isInteger( poll.correct_answer ) && <Typography fontWeight="500" fontSize="14px" component="em" padding="5px 10px" borderRadius="20px" display="flex" alignItems="center" bgcolor={palette.success.main} color={palette.success.contrastText} >Correct Answer: {ALPHABETS.at( +( poll.correct_answer || 0 ) )}</Typography>}
                        <Box display="flex" padding="10px" border={border[1]} borderRadius="20px" width="fit-content" bgcolor={palette.contentBg} flexWrap="wrap" gap="10px" alignItems="center">
                            <Typography variant='subtitle2' >
                                Show participants
                            </Typography>
                            <CustomSwitch size="small" color="secondary" checked={showParticipants} onChange={toggleResultView} />
                        </Box>
                    </Box>
                    {poll.options?.map( ( option, i ) => (
                        <Box marginBottom='5px' display="flex" borderRadius="5px" overflow="clip" alignItems="stretch" key={i}>
                            {!showParticipants && <Box padding="2px 5px" width="100%">
                                <Typography position="relative" zIndex={2} marginBottom="5px" component="div" display="block" fontSize="14px">
                                    <Latex delimiters={latexDelimiters}> {option}</Latex>
                                </Typography>

                                {/* VOTES INDICATOR */}
                                <Box overflow="clip" bgcolor={palette.grey[800]} width="100%" position="relative" padding="2px 5px" borderRadius="10px">
                                    <Typography position="relative" zIndex={2} display="block" fontSize="10px" variant='subtitle2'>{poll?.totalVotes > 0 ? `${Math.round( ( ( poll.votes?.[i]?.length || 0 ) * 100 ) / poll?.totalVotes )}%` : "0%"}</Typography>
                                    <Box position="absolute" zIndex={1} sx={{ background: `${POLL_OPTIONS_COLORS[i]}` }} borderRadius="0 20px 20px 0" left="0" bottom="0" top="0" width={`${( ( poll.votes?.[i]?.length || 0 ) * 100 ) / poll?.totalVotes}%`} height="100%" ></Box>
                                </Box>
                            </Box>}
                            {showParticipants && <Box borderRadius="10px" border={border[1]} position="relative" padding="10px" width="100%">
                                <Typography position="relative" zIndex={2} component="div" display="block" fontSize="14px">
                                    <Latex delimiters={latexDelimiters}> {option}</Latex>
                                </Typography>
                                {poll.votes[i]?.length > 0 && <Box>
                                    {poll.votes[i]?.map( voter => (
                                        <CustomChip key={voter.user_id} textStyles={{ textTransform: "capitalize" }} value={voter.name} />
                                    ) )}
                                </Box>}
                                {poll.votes[i]?.length === 0 && <Typography color='textSecondary' position="relative" zIndex={2} display="block" fontSize="12px" textTransform="capitalize" variant='subtitle2'> No Votes!</Typography>}
                            </Box>}
                        </Box>
                    ) )}
                </Box>
            </Collapse>
        </Box >
    )
}


const PollManager = observer( ( { open, setOpen, container } ) => {
    const [pollDurationAnchorEl, setPollDurationAnchorEl] = useState( null )
    const [nextScheduledPoll, setNextScheduledPoll] = useState( null )
    const [options, setOptions] = useState( [{ text: "Option 1" }, { text: "Option 2" }] )
    const [schedulerInterval, setSchedulerInterval] = useState( 0 )
    const [pollDuration, setPollDuration] = useState( 60 )
    const [question, setQuestion] = useState( "New poll" )
    const [mode, setMode] = useState( "manage-polls" )

    const { palette, border, layout, breakpoints } = useTheme()
    const { meeting_id } = useParams()

    const xs = useMediaQuery( breakpoints.down( 'sm' ) )

    // const { emitEvent } = useSocketsContext()

    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()
    const MeetingStore = useMeetingStore()


    const pollSchedulerTimerRef = useRef()
    const nextScheduledPollRef = useRef( null )
    const pollSchedulerStatusRef = useRef( false )


    const handleAddOption = () => {
        setOptions( [...options, { text: `Option ${options.length + 1}` }] )
    }

    const handleChangeOption = ( updateType, index, value ) => {
        const updated = [...options]
        if ( updateType === "answer_status" ) {
            updated.forEach( opt => opt.correct_answer = false )
            updated[index].correct_answer = value
        } else
            updated[index].text = value
        console.log( updated )

        setOptions( updated )
    }

    const handleSubmit = async ( toQueue = false ) => {

        const trimmedOptions = options.filter( opt => opt.text.trim() !== "" ).map( opt => opt.text )
        const correct_answer = options.findIndex( opt => Boolean( opt.correct_answer ) )

        if ( question && trimmedOptions.length >= 2 ) {

            if ( !toQueue ) {
                const { message, success, poll } = await OnlineSessionSocketStore.emit(
                    'poll:create',
                    {
                        room_id: meeting_id,
                        question: question,
                        options: trimmedOptions,
                        duration: pollDuration,
                        correct_answer: correct_answer >= 0 ? correct_answer : null
                    } )

                if ( success ) {
                    setQuestion( "New poll" )
                    setOptions( [{ text: "Option 1" }, { text: "Option 2" }] )

                    MeetingStore.addOrRemovePoll( poll )
                    setMode( 'manage-polls' )
                }
                if ( message )
                    toast( message )
            }
            else {
                const details = {
                    id: uuidv4(),
                    question: question,
                    options: trimmedOptions,
                    correct_answer: correct_answer >= 0 ? correct_answer : null
                }
                MeetingStore.updatePollQueue( "add", [details] )
                setMode( 'manage-poll-queue' )
            }

        }
    }

    const updatePollInterval = ( intervalInMinutes ) => {
        setSchedulerInterval( intervalInMinutes )
        localStorage.setItem( 'poll_queue_interval', intervalInMinutes )
    }

    useEffect( () => {
        const queue = MeetingStore.getPollQueue

        // 1. Stop Conditions
        if ( schedulerInterval <= 0 || queue.length === 0 ) {
            setNextScheduledPoll( null )
            nextScheduledPollRef.current = null
            pollSchedulerStatusRef.current = false
            if ( pollSchedulerTimerRef.current ) clearTimeout( pollSchedulerTimerRef.current )
            return
        }

        // 2. Logic: If no timer is running, set the initial target
        if ( !pollSchedulerStatusRef.current || !nextScheduledPollRef.current ) {
            nextScheduledPollRef.current = dayjs().add( schedulerInterval * 60, 'seconds' )
            setNextScheduledPoll( nextScheduledPollRef.current )
            pollSchedulerStatusRef.current = true
        }

        // 3. Calculate REMAINING time (this prevents the "push forward" bug)
        const msRemaining = nextScheduledPollRef.current.diff( dayjs() )

        // Clear any old timer before setting the adjusted one
        if ( pollSchedulerTimerRef.current )
            clearTimeout( pollSchedulerTimerRef.current )

        pollSchedulerTimerRef.current = setTimeout( async () => {
            pollSchedulerStatusRef.current = false
            const pollDetails = MeetingStore.updatePollQueue( "shift" )

            const { message, success, poll } = await OnlineSessionSocketStore.emit(
                'poll:create',
                {
                    room_id: meeting_id,
                    question: pollDetails.question,
                    options: pollDetails.options,
                    duration: Math.max( 90, 60 * schedulerInterval ),
                } )

            if ( success ) {
                MeetingStore.addOrRemovePoll( poll )
            }
            if ( message )
                toast( message )

        }, msRemaining )

        return () => {
            if ( pollSchedulerTimerRef.current ) clearTimeout( pollSchedulerTimerRef.current )
        }


    }, [MeetingStore.getPollQueue, MeetingStore, meeting_id, schedulerInterval] )


    useEffect( () => {
        MeetingStore.retrievePollQueue()
        setSchedulerInterval( +( localStorage.getItem( 'poll_queue_interval' ) || 0 ) )
    }, [MeetingStore] )

    return (
        <Dialog
            container={container.current}
            fullScreen={xs}
            slotProps={{
                container: {
                    sx: { alignItems: { sm: "center", xs: "flex-end" } }
                },
                paper: {
                    sx: {
                        width: { sm: "95%", xs: "100%" },
                        maxWidth: "1024px",
                        maxHeight: "90vh",
                        height: "fit-content",
                        display: "flex",
                        flexDirection: "column",
                        gap: layout.cardsGap,
                        padding: "20px"
                    }
                }
            }}
            open={open}
        >

            <Box paddingBottom="0" display="flex" flexDirection="column" gap={layout.cardsGap}>

                <Box display="flex" gap={layout.cardsGap} alignItems="flex-start" justifyContent="space-between"  >
                    <Box flexGrow={1} display="flex" justifyContent="space-between" flexWrap="wrap" gap="10px" alignItems="center">
                        <Box display="flex" gap="10px" alignItems="center">
                            {["create", "manage-poll-queue"].includes( mode ) && <IconButton onClick={() => setMode( 'manage-polls' )} size='small' sx={{ border: `1px solid ${palette.text.primary}` }} color='greyed' >
                                {Icons.default.ArrowBack}
                            </IconButton>}
                            <Box position="relative"  >
                                <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">{getPollManagerDialogTitle( mode ).title}</Typography>
                                <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >{getPollManagerDialogTitle( mode ).subtitle}</Typography>
                            </Box>
                        </Box>
                        {["manage-polls", "manage-poll-queue"].includes( mode ) && <ButtonGroup sx={{ display: { sm: "flex", xs: "none" } }} disableElevation variant='contained' >
                            {mode === "manage-polls" && <Button sx={{ borderRadius: "20px" }} onClick={() => setMode( 'create' )} startIcon={Icons.default.AddIcon}>
                                Create Poll
                            </Button>}
                            {mode === "manage-polls" && <Button sx={{ borderRadius: "20px" }} onClick={() => setMode( 'manage-poll-queue' )} startIcon={Icons.default.ViewListIcon}>
                                Manage Poll Queue
                            </Button>}
                        </ButtonGroup>}
                    </Box>
                    <IconButton onClick={() => setOpen( false )}>
                        {Icons.default.CloseIcon}
                    </IconButton>

                </Box>

                {["manage-polls", "manage-poll-queue"].includes( mode ) && <ButtonGroup sx={{ display: { sm: "none", xs: "flex" } }} disableElevation variant='contained' >
                    {mode === "manage-polls" && <Button sx={{ borderRadius: "20px" }} onClick={() => setMode( 'create' )} startIcon={Icons.default.AddIcon}>
                        Create Poll
                    </Button>}
                    {mode === "manage-polls" && <Button sx={{ borderRadius: "20px" }} onClick={() => setMode( 'manage-poll-queue' )} startIcon={Icons.default.ViewListIcon}>
                        Manage Poll Queue
                    </Button>}
                </ButtonGroup>}

                {<Box padding={["manage-polls", "manage-poll-queue"].includes( mode ) ? "10px" : "0"} maxHeight={["manage-polls", "manage-poll-queue"].includes( mode ) ? "1000px" : 0} overflow="hidden" bgcolor={palette.contentBg} gap={layout.cardsGap} borderRadius="10px" border={["manage-polls", "manage-poll-queue"].includes( mode ) ? border[1] : "none"} display="flex" justifyContent="space-between" alignItems={{ sm: "center", xs: "stretch" }} flexDirection={{ xs: "column", sm: "row" }}>
                    <NextPollTimer nextScheduledPoll={nextScheduledPoll} schedulerInterval={schedulerInterval} />
                    <Box display="flex" flexDirection="column" alignItems={{ xs: "flex-start", sm: "flex-end" }}>
                        <Button sx={{ borderRadius: "20px", width: { xs: "100%", sm: "max-content" } }} onClick={e => setPollDurationAnchorEl( e.currentTarget )} endIcon={Icons.default.KeyboardArrowDownIcon} startIcon={Icons.default.Clock} color="greyed" variant='outlined'>
                            <Typography variant='subtitle2'  >{autoPollIntervalOptions.find( dur => dur.value === schedulerInterval ).buttonLabel || autoPollIntervalOptions.find( dur => dur.value === schedulerInterval ).label}</Typography>
                        </Button>
                        <Menu disableAutoFocus disableEnforceFocus disableRestoreFocus onClose={() => setPollDurationAnchorEl( null )} anchorEl={pollDurationAnchorEl} transformOrigin={{ vertical: "bottom", horizontal: "left" }} anchorOrigin={{ vertical: "top", horizontal: "left" }} open={Boolean( pollDurationAnchorEl )}  >
                            {autoPollIntervalOptions.map( durationOption => (
                                <MenuItem onClick={() => updatePollInterval( durationOption.value )} dense selected={durationOption.value === schedulerInterval} sx={{ fontSize: "14px" }} value={durationOption.value} key={durationOption.value}>{durationOption.label}</MenuItem>
                            ) )}
                        </Menu>
                    </Box>
                </Box>}
            </Box>


            {mode === "manage-polls" && <Box flexGrow={1} className="custom-scrollbar" overflow="auto">
                {MeetingStore.getPolls?.length > 0 && <Box overflow="auto" display="flex" flexGrow={1} flexDirection="column" gap="5px">
                    {MeetingStore.getPolls?.map( poll => (
                        <PollBar poll={poll} key={poll.id} />
                    ) )}
                </Box>}

                {MeetingStore.getPolls.length === 0 && <Box minHeight="250px" display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap="10px" borderRadius="10px" bgcolor={palette.contentBg} padding="20px">
                    <Box component="img" src={pollsImg} height="120px" />
                    <Typography variant='subtitle2' textAlign="center">Polls are not created!</Typography>
                    <Box display="flex" gap="10px" alignItems="center" flexWrap="wrap">
                        <Button startIcon={Icons.default.Checklist} variant='outlined' onClick={() => setMode( 'create' )} color="greyed">Create Poll</Button>
                        <Button startIcon={Icons.default.ViewListIcon} variant='outlined' onClick={() => setMode( 'manage-poll-queue' )} color="greyed">Manage Poll Queue</Button>
                    </Box>
                </Box>}
            </Box>}

            {mode === "create" && <Box>
                <Box >
                    <FormControl fullWidth>
                        <TextField
                            type="text"
                            size='small'
                            value={question}
                            placeholder="Enter question"
                            onFocus={e => e.target.select()}
                            onChange={e => setQuestion( e.target.value )}
                        />
                    </FormControl>
                    <Box display="grid" marginTop="10px" gridTemplateColumns={{ md: "1fr 1fr", xs: "1fr" }} gap="10px" >
                        {options.map( ( opt, i ) => (
                            <Box border={border[1]} display="flex" borderRadius="5px" overflow="clip" bgcolor={palette.form.inputBg} alignItems="stretch" key={i}>
                                <Box width="40px" display="flex" alignItems="center" justifyContent="center" borderRight={border[1]} bgcolor={palette.contentBgDark}>
                                    <Typography display="inline" >{ALPHABETS.at( i )}</Typography>
                                </Box>
                                <Box width="40px" display="flex" alignItems="center" justifyContent="center" borderRight={border[1]} bgcolor={palette.contentBgDark}>
                                    <Checkbox size='small' onChange={event => handleChangeOption( "answer_status", i, event.target.checked )} checked={opt.correct_answer || false} />
                                </Box>
                                <FormControl fullWidth>
                                    <TextField
                                        variant="standard"
                                        slotProps={{ htmlInput: { sx: { padding: "0" } }, input: { disableUnderline: true, sx: { background: "rgba(0,0,0,0)", padding: "8px" } } }}
                                        size='small'
                                        type="text"
                                        value={opt.text}
                                        placeholder={`Option ${i + 1}`}
                                        onChange={e => handleChangeOption( "text", i, e.target.value )}
                                        autoFocus
                                        onFocus={e => e.target.select()}
                                    />
                                </FormControl>
                            </Box>
                        ) )}
                        {options.length < 10 && <Box onClick={handleAddOption} border={border[1]} sx={{ opacity: "0.6", cursor: "pointer", ":hover": { opacity: "1" } }} display="flex" borderRadius="5px" overflow="clip" bgcolor={palette.form.inputBg} alignItems="stretch" key={"addnew"}>
                            <Box width="40px" display="flex" alignItems="center" justifyContent="center" borderRight={border[1]} bgcolor={palette.contentBgDark}>
                                {Icons.default.AddIcon}
                            </Box>
                            <FormControl fullWidth>
                                <TextField
                                    sx={{}}
                                    variant="standard"
                                    slotProps={{ htmlInput: { readOnly: true, sx: { padding: "0", cursor: "pointer" } }, input: { disableUnderline: true, sx: { background: "rgba(0,0,0,0)", padding: "8px" } } }}
                                    size='small'
                                    type="text"
                                    aria-readonly
                                    value={""}
                                    placeholder={`Add New Option`}
                                />
                            </FormControl>
                        </Box>}
                    </Box>
                    <Box marginTop="20px">
                        <Typography gutterBottom variant='subtitle2' component="p">Set poll duration</Typography>
                        <Box display="flex" gap="10px" flexWrap="wrap">
                            {pollDurationOptions.map( poll => (
                                <Box onClick={() => setPollDuration( poll.value )} sx={{ cursor: "pointer" }} bgcolor={poll.value === pollDuration && palette.rowHover} padding="5px 10px" borderRadius="5px" border={poll.value === pollDuration ? `2px solid ${palette.secondary.dark}` : `2px dashed ${palette.borderColor}`} key={poll.value}>
                                    <Typography color='secondary' variant='subtitle2' component="p">{poll.duration}</Typography>
                                </Box>
                            ) )}
                        </Box>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" marginTop="40px" gap="10px" alignItems="center">
                        {/* <Button variant='outlined' color='error' disableElevation onClick={() => setMode( 'manage-polls' )}>Cancel Create</Button> */}
                        <Button variant='text' color='secondary' startIcon={Icons.default.ViewListIcon} onClick={() => handleSubmit( true )}>Add to Poll Queue</Button>
                        <Button variant='contained' color='primary' startIcon={Icons.default.AddIcon} disableElevation onClick={() => handleSubmit()}>Start Poll</Button>
                        {/* {poll && poll.isActive && <Button color="error" variant='contained' disableElevation onClick={closePoll}>Close Poll</Button>} */}
                    </Box>
                </Box>
            </Box>}
            {mode === "manage-poll-queue" && <PollQueue />}
        </Dialog>
    )
} )

export default PollManager
